import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { Language } from '../../types';
import { LANGUAGES } from '../../utils/constants';
import { LAB_NAV_HEIGHT } from './constants';

interface LabNavProps {
  onPartnerClick: () => void;
}

const NAV_LINKS = [
  { key: 'lab', target: 'lab-top' },
  { key: 'models', target: 'lab-asr' },
  { key: 'partnership', target: 'lab-partnership' },
  { key: 'sovereign', target: 'lab-governance' },
] as const;

const LabNav: React.FC<LabNavProps> = ({ onPartnerClick }) => {
  const { t, i18n } = useTranslation();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const currentLanguage = LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  const handleLanguageChange = (lang: Language) => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    localStorage.setItem('userSelectedLanguage', 'true');
    localStorage.setItem('i18nextLng', lang);
    setIsLangOpen(false);
    setIsMenuOpen(false);
  };

  const scrollTo = (e: React.MouseEvent, targetId: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    const el = document.getElementById(targetId);
    window.scrollTo({
      top: el ? Math.max(0, el.offsetTop - LAB_NAV_HEIGHT) : 0,
      behavior: 'smooth',
    });
  };

  return (
    <header className="fixed top-0 z-50 w-full border-b border-white/[0.08] bg-lab-night/30 backdrop-blur-xl">
      <nav className="mx-auto flex h-16 w-full max-w-[1360px] items-center justify-between px-5 md:px-14">
        {/* Brand: field-glass mark + wordmark */}
        <a href="#lab-top" onClick={(e) => scrollTo(e, 'lab-top')} className="flex items-center gap-2.5">
          <span
            title={t('lab.nav.markTitle')}
            className="grid h-[18px] w-[18px] place-items-center rounded-full border-2 border-lab-frost"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-lab-asr" />
          </span>
          <span className="text-lg font-semibold tracking-tight text-lab-frost">duran</span>
          <span className="hidden font-plexmono text-[10.5px] text-lab-frost/50 sm:block">
            {t('lab.nav.tag')}
          </span>
        </a>

        {/* Desktop links */}
        <div className="hidden items-center gap-[30px] text-[13.5px] text-lab-frost/70 md:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.key}
              href={`#${link.target}`}
              onClick={(e) => scrollTo(e, link.target)}
              className="transition-colors hover:text-lab-frost"
            >
              {t(`lab.nav.${link.key}`)}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div className="relative hidden md:block">
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="rounded-full border border-white/20 px-3 py-1.5 font-plexmono text-[11px] uppercase text-lab-frost/80 transition-colors hover:border-white/40"
            >
              {currentLanguage.code.toUpperCase()} ▾
            </button>
            {isLangOpen && (
              <div className="absolute right-0 top-full mt-2 max-h-[60vh] min-w-[180px] overflow-y-auto rounded-xl border border-white/10 bg-lab-ink/95 py-1 shadow-2xl backdrop-blur-xl">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className="block w-full whitespace-nowrap px-4 py-2 text-left text-[13px] text-lab-frost/80 transition-colors hover:bg-white/10 hover:text-lab-frost"
                  >
                    {lang.flag} {lang.nativeName}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={onPartnerClick}
            className="lab-siri hidden rounded-full border border-white/35 px-5 py-2 text-[13.5px] font-semibold text-lab-frost transition-colors hover:border-white/60 hover:bg-white/5 md:block"
          >
            {t('lab.nav.cta')}
          </button>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 text-lab-frost md:hidden"
            aria-label={t('lab.nav.menu')}
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="border-t border-white/[0.08] bg-lab-night/90 backdrop-blur-xl md:hidden">
          <div className="flex flex-col gap-1 px-5 py-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.key}
                href={`#${link.target}`}
                onClick={(e) => scrollTo(e, link.target)}
                className="rounded-lg px-2 py-3 text-[15px] text-lab-frost/80 transition-colors hover:bg-white/5 hover:text-lab-frost"
              >
                {t(`lab.nav.${link.key}`)}
              </a>
            ))}
            <button
              onClick={() => {
                setIsMenuOpen(false);
                onPartnerClick();
              }}
              className="mt-2 rounded-full bg-lab-frost px-5 py-3 text-[14px] font-semibold text-lab-ink"
            >
              {t('lab.nav.cta')}
            </button>
            <select
              value={i18n.language}
              onChange={(e) => handleLanguageChange(e.target.value as Language)}
              className="mt-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 font-plexmono text-[12px] text-lab-frost"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code} className="bg-lab-ink">
                  {lang.flag} {lang.nativeName}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </header>
  );
};

export default LabNav;
